import { Link } from '@tanstack/react-router'
import { Seo } from '@saas-pages/shared/public/seo'
import { useLocale, useT } from '@/shared/i18n'
import { seoFor } from '@/seo/routes'
import { getAllPosts } from '@/shared/blog/posts'

function formatDate(iso: string, locale: 'ru' | 'en'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export function BlogIndexPage() {
  const t = useT()
  const { locale } = useLocale()
  const posts = getAllPosts(locale)

  return (
    <>
      <Seo {...seoFor('blog', locale)} />

      <div className="mx-auto max-w-3xl">
        <nav aria-label="breadcrumb" className="mb-6 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center">
            <li>
              <Link to="/" className="hover:text-foreground">
                pdfy.ru
              </Link>
            </li>
            <li aria-hidden="true" className="mx-2">/</li>
            <li aria-current="page" className="text-foreground">
              {t.blog.breadcrumb}
            </li>
          </ol>
        </nav>

        <h1 className="mb-3 text-3xl font-bold tracking-tight sm:text-4xl">
          {t.blog.indexH1}
        </h1>
        <p className="mb-10 text-muted-foreground">
          {t.blog.indexIntro}
        </p>

        {posts.length === 0 ? (
          <p className="text-muted-foreground">{t.blog.empty}</p>
        ) : (
          <ul className="space-y-6">
            {posts.map((post) => (
              <li key={post.slug}>
                <article className="rounded-xl border bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
                  <Link
                    to="/blog/$slug"
                    params={{ slug: post.slug }}
                    className="group block"
                  >
                    <h2 className="text-xl font-semibold tracking-tight group-hover:text-primary sm:text-2xl">
                      {post.title}
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {post.description}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <time dateTime={post.date}>{formatDate(post.date, locale)}</time>
                      {post.tags.length > 0 && (
                        <>
                          <span aria-hidden>·</span>
                          <span>{post.tags.join(', ')}</span>
                        </>
                      )}
                    </div>
                  </Link>
                </article>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
